// 배너 관리 페이지.
// 카드형 드래그앤드롭 정렬 목록 + 신규 등록 + 인라인 활성 토글 + 수정/삭제.
//  - 노출 순서를 드래그(또는 위/아래 버튼)로 바꾸고 "순서 저장" 으로 일괄 반영한다(sort_order).
//  - 정렬 작업 중(dirty) 에는 서버 갱신으로 작업 순서를 덮어쓰지 않는다(드래그 유실 방지).
//    토글/삭제는 작업 목록에도 즉시 반영(낙관적)해 dirty 상태와 정합을 유지한다.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, GripVertical, Plus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BANNER_LINK_KIND, type BannerDto } from "@oo/shared";
import { Badge, useConfirm } from "@oo/ui";
import { PageHeader } from "@/shared/ui";
import {
  deleteBanner,
  listBannersAdmin,
  reorderBanners,
  updateBanner,
} from "@/features/catalog/catalogApi.ts";
import { imageUrl } from "@/features/catalog/imageUrl.ts";

const LINK_KIND_LABEL: Record<string, string> = {
  product: "상품 연결",
  category: "카테고리 연결",
  external: "외부 링크",
  none: "링크 없음",
};

// 배열에서 from 위치 항목을 to 위치로 옮긴 새 배열을 만든다.
function move<T>(arr: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) {
    return arr;
  }
  const next = [...arr];
  const [m] = next.splice(from, 1);
  if (!m) return arr;
  next.splice(to, 0, m);
  return next;
}

// 배너 목록/정렬 관리 화면.
export default function BannersPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data: banners, isLoading } = useQuery({
    queryKey: ["admin", "banners"],
    queryFn: listBannersAdmin,
  });

  // 드래그로 바뀌는 작업 순서. dirty 가 아닐 때만 서버 데이터로 동기화한다.
  const [working, setWorking] = useState<BannerDto[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (banners && !dirty) {
      setWorking(banners);
    }
  }, [banners, dirty]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "banners"] });

  const saveMut = useMutation({
    mutationFn: () => reorderBanners({ orderedIds: working.map((b) => b.id) }),
    onSuccess: async () => {
      setDirty(false);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
      await invalidate();
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "순서 저장 실패"),
  });

  // 활성 토글: 작업 목록에도 즉시 반영(dirty 중 유실 방지) + 서버 반영.
  const toggleMut = useMutation({
    mutationFn: (b: BannerDto) => updateBanner(b.id, { isActive: !b.isActive }),
    onSuccess: () => invalidate(),
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "토글 실패"),
  });

  const removeMut = useMutation({
    mutationFn: (id: string) => deleteBanner(id),
    onSuccess: () => invalidate(),
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "삭제 실패"),
  });

  // 드래그 중인 항목 인덱스(ref) + 드롭 대상 표시(state).
  const dragIndex = useRef<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const onReorder = (next: BannerDto[]) => {
    setWorking(next);
    setDirty(true);
    setError(null);
  };

  return (
    <div className="mx-auto flex max-w-screen-lg flex-col gap-4 px-4 py-6 sm:px-6 sm:py-8">
      <PageHeader
        title="배너"
        description="메인 화면에 노출되는 배너를 등록하고, 드래그로 노출 순서를 바꿉니다."
        actions={
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center">
            {saved ? (
              <span className="col-span-2 text-xs text-success-700 sm:col-span-1">저장됨</span>
            ) : null}
            <button
              type="button"
              disabled={!dirty || saveMut.isPending}
              onClick={() => saveMut.mutate()}
              className="h-10 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-40"
            >
              {saveMut.isPending ? "저장 중..." : "순서 저장"}
            </button>
            <Link
              to="/catalog/banners/new"
              className="inline-flex min-h-touch items-center justify-center gap-2 rounded bg-brand-600 px-3 text-sm font-semibold text-white transition-colors duration-fast hover:bg-brand-700"
            >
              <Plus size={18} aria-hidden="true" />
              신규 배너 등록
            </Link>
          </div>
        }
      />

      {error ? <p className="text-sm text-danger-700">{error}</p> : null}

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-ink-100" />
          ))}
        </div>
      ) : working.length === 0 ? (
        <p className="rounded-md border border-dashed border-ink-300 p-10 text-center text-sm text-ink-600">
          등록된 배너가 없습니다.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {working.map((b, idx) => {
            const url = imageUrl(b.imageKey);
            const isOver = overIndex === idx;
            return (
              <li
                key={b.id}
                draggable
                onDragStart={() => {
                  dragIndex.current = idx;
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (overIndex !== idx) setOverIndex(idx);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = dragIndex.current;
                  if (from !== null && from !== idx) {
                    onReorder(move(working, from, idx));
                  }
                  dragIndex.current = null;
                  setOverIndex(null);
                }}
                onDragEnd={() => {
                  dragIndex.current = null;
                  setOverIndex(null);
                }}
                className={`grid min-w-0 grid-cols-[auto_auto_minmax(0,1fr)] items-center gap-2 rounded-lg border bg-white p-3 sm:flex sm:gap-3 ${
                  isOver ? "border-brand-600 bg-brand-50" : "border-ink-200"
                }`}
              >
                {/* 드래그 핸들 */}
                <span className="shrink-0 cursor-grab text-ink-400" aria-hidden="true">
                  <GripVertical size={20} />
                </span>
                {/* 순번 */}
                <span className="w-6 shrink-0 text-center text-sm font-semibold tabular-nums text-ink-500">
                  {idx + 1}
                </span>
                {/* 미리보기 */}
                <div className="col-span-3 h-32 w-full overflow-hidden rounded border border-ink-200 bg-ink-50 sm:col-span-1 sm:h-14 sm:w-32 sm:shrink-0">
                  {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : null}
                </div>
                {/* 연결/기간/상태 */}
                <div className="col-span-3 flex min-w-0 flex-1 flex-col gap-0.5 sm:col-span-1">
                  <span className="truncate font-medium text-ink-900">
                    {LINK_KIND_LABEL[b.linkKind] ?? b.linkKind}
                  </span>
                  {b.linkKind === BANNER_LINK_KIND.EXTERNAL && b.linkUrl ? (
                    <span className="truncate text-xs text-ink-600">{b.linkUrl}</span>
                  ) : b.linkTargetId ? (
                    <span className="truncate text-xs text-ink-600">대상 ID: {b.linkTargetId}</span>
                  ) : null}
                  <div className="mt-0.5 flex items-center gap-2">
                    <Badge tone={b.isActive ? "success" : "neutral"}>
                      {b.isActive ? "활성" : "비활성"}
                    </Badge>
                    <span className="text-xs text-ink-600">
                      {!b.startsAt && !b.endsAt
                        ? "무제한"
                        : `${b.startsAt ? `${b.startsAt} ` : ""}${b.endsAt ? `~ ${b.endsAt}` : ""}`}
                    </span>
                  </div>
                </div>
                {/* 위/아래 이동(보조 수단) */}
                <div className="flex shrink-0 justify-self-end sm:flex-col">
                  <button
                    type="button"
                    aria-label="위로"
                    disabled={idx === 0}
                    onClick={() => onReorder(move(working, idx, idx - 1))}
                    className="rounded p-0.5 text-ink-600 hover:bg-ink-100 disabled:opacity-30"
                  >
                    <ChevronUp size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label="아래로"
                    disabled={idx === working.length - 1}
                    onClick={() => onReorder(move(working, idx, idx + 1))}
                    className="rounded p-0.5 text-ink-600 hover:bg-ink-100 disabled:opacity-30"
                  >
                    <ChevronDown size={16} aria-hidden="true" />
                  </button>
                </div>
                {/* 인라인 관리 */}
                <div className="col-span-2 col-start-2 grid shrink-0 grid-cols-3 gap-1 text-xs sm:col-span-1 sm:flex sm:flex-col">
                  <button
                    type="button"
                    onClick={() => {
                      // 작업 목록에도 즉시 반영해 dirty 중에도 일관성 유지.
                      setWorking((prev) =>
                        prev.map((it) => (it.id === b.id ? { ...it, isActive: !it.isActive } : it)),
                      );
                      toggleMut.mutate(b);
                    }}
                    className="rounded border border-ink-300 px-2 py-1 hover:bg-ink-100"
                  >
                    {b.isActive ? "비활성화" : "활성화"}
                  </button>
                  <Link
                    to={`/catalog/banners/${b.id}`}
                    className="rounded border border-ink-300 px-2 py-1 text-center hover:bg-ink-100"
                  >
                    수정
                  </Link>
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = await confirm({
                        title: "배너 삭제",
                        description: "이 배너를 삭제하시겠습니까?",
                        confirmLabel: "삭제",
                        tone: "danger",
                      });
                      if (ok) {
                        setWorking((prev) => prev.filter((it) => it.id !== b.id));
                        removeMut.mutate(b.id);
                      }
                    }}
                    className="rounded border border-ink-300 px-2 py-1 text-danger-700 hover:bg-danger-50"
                  >
                    삭제
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
